import { mapDateStringToNgbDateStruct, mapNgbDateStructToDateString } from '@shared-ikp/utils/date.utils';
import { Adresse } from '../../../../generated/model/adresse.model';
import { BeteiligtePartei } from '../../../../generated/model/beteiligte-partei.model';
import { Kunde } from '../../../../generated/model/kunde.model';
import { Privatperson } from '../../../../generated/model/privatperson.model';
import { AdresseFormValue } from '../schemas';
import { Address, createAddress } from '@mwe/models';
import {
  PrivatpersonKontaktdatenFormValue,
  PrivatUnternehmenFormValue,
  RechnungsempfaengerFormValue,
  UnternehmenKontaktdatenFormValue,
} from '../schemas/common-vertragspartner.schema';
import { AccountVertragspartnerFormValue, VerwalteterVertragspartnerFormValue } from '../schemas/neuer-vertragspartner.schema';

export function mapKundeToPrivatUnternehmenFormValue(bisherigerKunde: Kunde | null | undefined): PrivatUnternehmenFormValue {
  if (bisherigerKunde?.kundeTyp === Kunde.KundeTypEnum.UNBEKANNT) {
    return {
      vertragspartnerTyp: null,
      privatpersonKontaktdaten: null,
      unternehmenKontaktdaten: null,
      rechnungsempfaenger: initialNeuerRechnungsempfaengerFormValue(),
    };
  }

  const privatUnternehmen: PrivatUnternehmenFormValue = {
    vertragspartnerTyp: 'privat',
    privatpersonKontaktdaten: initalPrivatpersonKontaktdatenFormValue(),
    unternehmenKontaktdaten: initialUnternehmenKontaktdatenFormValue(),
    rechnungsempfaenger: initialNeuerRechnungsempfaengerFormValue(),
  };

  if (isPrivatperson(bisherigerKunde?.vertragspartner)) {
    const privatpersonKontaktdaten = privatUnternehmen.privatpersonKontaktdaten ?? initalPrivatpersonKontaktdatenFormValue();

    privatUnternehmen.vertragspartnerTyp = 'privat';
    privatpersonKontaktdaten.vorname = bisherigerKunde.vertragspartner.vorname ?? '';
    privatpersonKontaktdaten.nachname = bisherigerKunde.vertragspartner.nachname ?? '';
    privatpersonKontaktdaten.email = bisherigerKunde.email ?? '';
    privatpersonKontaktdaten.telefon = bisherigerKunde.telefon ?? '';
    privatpersonKontaktdaten.geburtsdatum = mapDateStringToNgbDateStruct(bisherigerKunde.vertragspartner.geburtsdatum);
    privatUnternehmen.privatpersonKontaktdaten = privatpersonKontaktdaten;
  } else if (bisherigerKunde?.vertragspartner) {
    const unternehmenKontaktdaten = privatUnternehmen.unternehmenKontaktdaten ?? initialUnternehmenKontaktdatenFormValue();

    privatUnternehmen.vertragspartnerTyp = 'unternehmen';
    unternehmenKontaktdaten.name = bisherigerKunde.vertragspartner.name ?? '';
    unternehmenKontaktdaten.geburtsdatum = mapDateStringToNgbDateStruct(bisherigerKunde.vertragspartner.geburtsdatum);
    unternehmenKontaktdaten.uid = bisherigerKunde.vertragspartner.uid ?? '';
    unternehmenKontaktdaten.firmenbuchnummer = bisherigerKunde.vertragspartner.firmenbuchnummer ?? '';
    unternehmenKontaktdaten.email = bisherigerKunde.email ?? '';
    unternehmenKontaktdaten.telefon = bisherigerKunde.telefon ?? '';
    privatUnternehmen.unternehmenKontaktdaten = unternehmenKontaktdaten;
  }

  return privatUnternehmen;
}

export function mapPrivatUnternehmenFormValueToKunde(
  privatUnternehmen: PrivatUnternehmenFormValue,
  adresseFormValue: AdresseFormValue | null,
): Kunde {
  const rechnungsempfaenger = privatUnternehmen?.rechnungsempfaenger;
  const hasAlternativeRechnungsempfaenger = !!rechnungsempfaenger?.hasAbweichendenRechnungsempfaenger;
  const alternativeRechnungsempfaenger = hasAlternativeRechnungsempfaenger
    ? mapRechnungsempfaengerFormValueToUnternehmen(rechnungsempfaenger)
    : null;

  if (privatUnternehmen?.vertragspartnerTyp === 'privat') {
    return {
      vertragspartner: {
        vorname: privatUnternehmen?.privatpersonKontaktdaten?.vorname.trim() ?? '',
        nachname: privatUnternehmen?.privatpersonKontaktdaten?.nachname.trim() ?? '',
        geburtsdatum: mapNgbDateStructToDateString(privatUnternehmen?.privatpersonKontaktdaten?.geburtsdatum ?? null),
      },
      kundeTyp: Kunde.KundeTypEnum.EXTERNER_KUNDE,
      email: privatUnternehmen?.privatpersonKontaktdaten?.email.trim() ?? '',
      telefon: privatUnternehmen?.privatpersonKontaktdaten?.telefon.trim() ?? '',
      rechnungsempfaenger: alternativeRechnungsempfaenger ?? {
        vorname: privatUnternehmen?.privatpersonKontaktdaten?.vorname.trim() ?? '',
        nachname: privatUnternehmen?.privatpersonKontaktdaten?.nachname.trim() ?? '',
        adresse: mapAdresseFormValueToAdresse(adresseFormValue),
      },
    };
  }

  return {
    kundeTyp: Kunde.KundeTypEnum.EXTERNER_KUNDE,
    vertragspartner: {
      name: privatUnternehmen?.unternehmenKontaktdaten?.name.trim() ?? '',
      kundennummer: null,
      firmenbuchnummer: privatUnternehmen?.unternehmenKontaktdaten?.firmenbuchnummer.trim() ?? '',
      uid: privatUnternehmen?.unternehmenKontaktdaten?.uid.trim() ?? '',
      geburtsdatum: mapNgbDateStructToDateString(privatUnternehmen?.unternehmenKontaktdaten?.geburtsdatum ?? null),
      adresse: mapAdresseFormValueToAdresse(adresseFormValue),
    },
    email: privatUnternehmen?.unternehmenKontaktdaten?.email.trim() ?? '',
    telefon: privatUnternehmen?.unternehmenKontaktdaten?.telefon.trim() ?? '',
    rechnungsempfaenger: alternativeRechnungsempfaenger ?? {
      name: privatUnternehmen?.unternehmenKontaktdaten?.name.trim() ?? '',
      firmenbuchnummer: privatUnternehmen?.unternehmenKontaktdaten?.firmenbuchnummer.trim() ?? '',
      uid: privatUnternehmen?.unternehmenKontaktdaten?.uid.trim() ?? '',
      adresse: mapAdresseFormValueToAdresse(adresseFormValue),
    },
  };
}

function mapRechnungsempfaengerFormValueToUnternehmen(rechnungsempfaenger: RechnungsempfaengerFormValue): BeteiligtePartei {
  return {
    name: rechnungsempfaenger.name.trim(),
    kundennummer: rechnungsempfaenger.kundennummer.trim(),
    uid: rechnungsempfaenger.uid.trim(),
    firmenbuchnummer: rechnungsempfaenger.firmenbuchnummer.trim(),
    adresse: mapAdresseFormValueToAdresse(rechnungsempfaenger.adresse),
  };
}

export function initalPrivatpersonKontaktdatenFormValue(): PrivatpersonKontaktdatenFormValue {
  return {
    vorname: '',
    nachname: '',
    email: '',
    telefon: '',
    geburtsdatum: null,
  };
}

export function initialUnternehmenKontaktdatenFormValue(): UnternehmenKontaktdatenFormValue {
  return {
    name: '',
    geburtsdatum: null,
    uid: '',
    firmenbuchnummer: '',
    email: '',
    telefon: '',
  };
}

export function initialNeuerRechnungsempfaengerFormValue(): RechnungsempfaengerFormValue {
  return {
    hasAbweichendenRechnungsempfaenger: false,
    name: '',
    kundennummer: '',
    adresse: mapAdresseToFormValue(null),
    uid: '',
    firmenbuchnummer: '',
  };
}

export function initialVerwalteterVertragspartnerFormValue(): VerwalteterVertragspartnerFormValue {
  return {
    name: null,
    kundennummer: null,
    firmenbuchnummer: null,
    uid: null,
    adresse: mapAdresseToFormValue(null),
    email: null,
    telefon: null,
    rechnungsempfaenger: initialNeuerRechnungsempfaengerFormValue(),
  };
}

export function initialAccountVertragspartnerFormValue(): AccountVertragspartnerFormValue {
  return {
    name: '',
    kundennummer: '',
    adresse: mapAdresseToFormValue(null),
    uid: '',
    firmenbuchnummer: '',
    rechnungsempfaenger: initialNeuerRechnungsempfaengerFormValue(),
  };
}

function isPrivatperson(partei: BeteiligtePartei | null | undefined): partei is Privatperson {
  return partei !== undefined && partei !== null && 'vorname' in partei && 'nachname' in partei;
}

export function mapAdresseFormValueToAdresse(formValue?: AdresseFormValue): Adresse {
  return {
    plz: formValue?.plz?.trim(),
    ort: formValue?.ort?.trim(),
    strasse: formValue?.strasse?.trim(),
    hausnummer: formValue?.hausnummer?.trim(),
    stiege: formValue?.stiege?.trim(),
    tuernummer: formValue?.tuernummer?.trim(),
  };
}

export function mapAdresseToFormValue(adresse: Adresse | null | undefined): AdresseFormValue {
  return {
    plz: adresse?.plz ?? '',
    ort: adresse?.ort ?? '',
    strasse: adresse?.strasse ?? '',
    hausnummer: adresse?.hausnummer ?? '',
    stiege: adresse?.stiege ?? '',
    tuernummer: adresse?.tuernummer ?? '',
  };
}

export function parseAdresseFormValue(adresseFormValue: AdresseFormValue): Address {
  if (!adresseFormValue) {
    return null;
  }

  return createAddress({
    id: '',
    street: adresseFormValue?.strasse,
    streetNumber: adresseFormValue?.hausnummer,
    block: adresseFormValue?.stiege,
    doorNumber: adresseFormValue?.tuernummer,
    postcode: adresseFormValue?.plz,
    city: adresseFormValue?.ort,
    country: null,
  });
}
